import get from 'lodash/get';
import set from 'lodash/set';

/*
 * Returns  a function that takes an object and returns an
 * object with values from the old object mapped to new locations.
 *
 * At its simplest
 *
 *   const reshaper = makeReshaper({ b: 'a'})
 *   reshaper({ a: true }) --> { b: true }
 *
 * That is, the input to makeReshaper() is a mapping of old to new
 * property names.
 *
 * Note: These are arranged in the order `new: old`, rather than
 * `old: new` so it is easier to see the shape of the resulting
 * object in the code.
 *
 * These names may be dotted to retrieve or put values
 * into arbitrary locations. e.g.:
 *
 *   const reshaper = makeReshaper({ 'x.y': 'a', 'b': 'm.n' })
 *   reshaper({ a: true, m: { n: 2 } }) --> { x: { y: true }, b: 2 }
 *
 * Note that properties in the provided object that are not in the mapping
 * are ignored. Properties in the mapping but not in the target object, are set to
 * undefined
 *
 * Additionally, old properties can be represented as functions which allows for
 * more complex values.
 *
 *   const reshaper = makeReshaper({
 *     hasAlerts: obj => obj.alerts > 0,
 *   });
 *   reshaper({ alerts: 2 }) --> { hasAlerts: true }
 *
 * You can also provide a second argument to makeReshaper. This should contain
 * any properties you want to add to the resulting object that are not from the original.
 * Most commonly, this will be used to add a `type` property to the object.
 */
export const makeReshaper = (reshapeRules, propsToAdd = {}) => {
  if (!reshapeRules || typeof reshapeRules !== 'object') {
    return () => ({});
  }

  return (oldObj) => {
    const reshaped = Object.entries(reshapeRules).reduce((newObj, [newKey, oldKey]) => {
      const newValue = typeof oldKey === 'function' ? oldKey(oldObj) : get(oldObj, oldKey);

      return set(newObj, newKey, newValue);
    }, {});

    return {
      ...reshaped,
      ...propsToAdd,
    };
  };
};
